import React, { Fragment } from 'react';
import { useTranslation } from 'react-i18next';

import { Amount } from '@klp/design/Amount';
import { DateTime } from '@klp/design/DateTime';
import { KeyValueItem, KeyValueItems } from '@klp/keyvalueitem';

import type { ByggKontrakt } from '../classes';

export interface IByggKontraktSummaryProps {
    kontrakt: ByggKontrakt;
}

export const ByggKontraktSummary: React.FC<IByggKontraktSummaryProps> = ({
    kontrakt,
}) => {
    const { t } = useTranslation();
    const garantiForMultipleContracts =
        kontrakt.kontraktGarantier
            .map((k) => k.kontraktId)
            .filter((v, i, a) => a.indexOf(v) === i).length !== 1;

    return (
        <KeyValueItems>
            {kontrakt.arligLeie > 0 && (
                <KeyValueItem
                    title="kontrakt.arligLeie"
                    data-testid="Kontrakt__arligLeie"
                >
                    <Amount value={kontrakt.arligLeie} /> &#8239;{' '}
                    {kontrakt.lokalValuta}
                    {t('kontrakt.perAar')}
                </KeyValueItem>
            )}

            {kontrakt.arligEiendomsskatt > 0 && (
                <KeyValueItem
                    title="kontrakt.arligEiendomskatt"
                    data-testid="Kontrakt__arligEiendomskatt"
                >
                    <Amount value={kontrakt.arligEiendomsskatt} />
                    &#8239; {kontrakt.lokalValuta}
                    {t('kontrakt.perAar')}
                </KeyValueItem>
            )}

            {kontrakt.arligFellesKostnad > 0 && (
                <KeyValueItem
                    title="kontrakt.arligFellesKostnad"
                    data-testid="Kontrakt__arligFellesKostnad"
                >
                    <Amount value={kontrakt.arligFellesKostnad} />
                    &#8239; {kontrakt.lokalValuta}
                    {t('kontrakt.perAar')}
                </KeyValueItem>
            )}

            {kontrakt.energiForbrukPrM2 > 0 && (
                <KeyValueItem
                    title="kontrakt.energiForbrukPrM2"
                    data-testid="Kontrakt__energiForbrukPrM2"
                >
                    {kontrakt.energiForbrukPrM2}
                </KeyValueItem>
            )}

            {kontrakt.felleskostnaderPrM2 > 0 && (
                <KeyValueItem
                    title="kontrakt.felleskostnaderPrM2"
                    data-testid="Kontrakt__felleskostnaderPrM2"
                >
                    <Amount value={kontrakt.felleskostnaderPrM2} />
                </KeyValueItem>
            )}

            {kontrakt.kostPrPplassPrAr > 0 && (
                <KeyValueItem
                    title="kontrakt.kostPrPplassPrAr"
                    data-testid="Kontrakt__kostPrPplassPrAr"
                >
                    <Amount value={kontrakt.kostPrPplassPrAr} />
                </KeyValueItem>
            )}

            {kontrakt.kontraktsnummer && (
                <KeyValueItem
                    title="kontrakt.kontraktsnummer"
                    data-testid="Kontrakt__kontraktsnummer"
                >
                    {kontrakt.kontraktsnummer}
                </KeyValueItem>
            )}

            {kontrakt.kontraktsdato && (
                <KeyValueItem
                    title="kontrakt.kontraktsdato"
                    data-testid="Kontrakt__kontraktsdato"
                >
                    <DateTime date={kontrakt.kontraktsdato} />
                </KeyValueItem>
            )}

            {kontrakt.gyldigFra && (
                <KeyValueItem
                    title="kontrakt.gyldigFra"
                    data-testid="Kontrakt__gyldigFra"
                >
                    <DateTime date={kontrakt.gyldigFra} />
                </KeyValueItem>
            )}

            {kontrakt.gyldigTil && (
                <KeyValueItem
                    title="kontrakt.gyldigTil"
                    data-testid="Kontrakt__gyldigTil"
                >
                    <DateTime date={kontrakt.gyldigTil} />
                </KeyValueItem>
            )}

            {kontrakt.kontraktFra && (
                <KeyValueItem
                    title="kontrakt.kontraktFra"
                    data-testid="Kontrakt__kontraktFra"
                >
                    <DateTime date={kontrakt.kontraktFra} />
                </KeyValueItem>
            )}

            {kontrakt.kontraktTil && (
                <KeyValueItem
                    title="kontrakt.kontraktTil"
                    data-testid="Kontrakt__kontraktTil"
                >
                    <DateTime date={kontrakt.kontraktTil} />
                </KeyValueItem>
            )}

            {kontrakt.kontraktGarantier.length > 0 && (
                <KeyValueItem title="kontrakt.kontraktGaranti">
                    {kontrakt.kontraktGarantier.slice(0, 5).map((garanti) => (
                        <Fragment key={garanti.garantiId}>
                            <Amount value={garanti.amount} />
                            &#8239; {kontrakt.lokalValuta}
                            {' (' +
                                (garantiForMultipleContracts
                                    ? garanti.kontraktId + ', '
                                    : '') +
                                garanti.klassifiseringstype +
                                ')'}
                            <br />
                        </Fragment>
                    ))}
                    {kontrakt.kontraktGarantier.length > 5 && '...'}
                </KeyValueItem>
            )}
        </KeyValueItems>
    );
};
